import QueryFetchStatePage from "@components/QueryFetchStatePage";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import VideoPreview from "@components/shared/VideoPreview";
import { Button } from "@components/ui/button";
import { usePublicVideos } from "@hooks/usePublicVideos";
import { ScreenPlayingOptionType } from "@hooks/useScreens/types";
import { useVideoAssets } from "@hooks/useVideoAssets";
import { cn } from "@utils/index";
import { useEffect, useState } from "react";
import { FiPlay } from "react-icons/fi";
import { ImFileVideo } from "react-icons/im";
import { IoReload } from "react-icons/io5";
import { MdDone } from "react-icons/md";

function SelectVideo({
  selected,
  onSelected,
  listOnly = false,
}: {
  selected?: ScreenPlayingOptionType[];
  onSelected: (assets: ScreenPlayingOptionType[]) => void;
  listOnly?: boolean;
}) {
  const {
    assets,
    assetsFetchStatus,
    getAssets,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useVideoAssets();
  const {
    Videos: publicAssets,
    VideosFetchStatus: publicAssetsFetchStatus,
    getVideos: getPublicAssets,
    hasNextPage: hasNextPublicPage,
    fetchNextPage: fetchNextPublicPage,
    isFetchingNextPage: isFetchingNextPublicPage,
  } = usePublicVideos();

  const [publicAssetsSelected, setPublicAssetsSelected] = useState(false);

  const videoAssets = listOnly ? [...assets, ...publicAssets] : publicAssetsSelected ? publicAssets : assets
  useEffect(() => {
    if (assets.length === 0) {
      setPublicAssetsSelected(true);
    }
  }, [assets]);

  return (
    <QueryFetchStatePage
      queryFetchStatus={assetsFetchStatus}
      onRetry={getAssets}
    >
      <QueryFetchStatePage
        queryFetchStatus={publicAssetsFetchStatus}
        onRetry={getPublicAssets}
      >
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 sm:gap-2">
          <div className="flex w-full items-center justify-between">
            {
              !listOnly && (
                <div className="flex gap-2">
                  {assets.length > 0 &&
                    tab("Your Videos", !publicAssetsSelected, () => {
                      setPublicAssetsSelected(false);
                    })}
                  {tab("Browse Free Videos", publicAssetsSelected, () => {
                    setPublicAssetsSelected(true);
                  })}
                </div>
              )
            }
          </div>

          <div className="h-full w-full flex-1 flex-col items-center justify-center gap-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6 sm:gap-2 lg:grid-cols-3 xl:grid-cols-4">
              {videoAssets.map((asset) => {
                const _isSelected = selected?.some(a => a.id === asset.id);
                return (
                  <button
                    className={cn(
                      "relative w-full h-32 overflow-clip rounded-md border-2 bg-lightCard shadow-sm dark:bg-darkCard",
                      _isSelected
                        ? "border-accentColor dark:border-darkAccentColor"
                        : "border-borderColor dark:border-darkBorderColor",
                      listOnly && "h-12",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (_isSelected) {
                        onSelected(selected?.filter(a => a.id !== asset.id) || []);
                      } else {
                        const newSelected = [...(selected || []), asset]
                        onSelected(
                          newSelected
                            .map(s => ({ ...s, type: "video" } as ScreenPlayingOptionType))
                        );
                      }
                    }}
                  >
                    <div className="flex h-full w-full items-center justify-center bg-black/50">
                      {
                        asset.thumbnail ? <img src={asset.thumbnail} className="h-full w-full" /> :
                          <ImFileVideo className="text-4xl text-white" />
                      }
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-2">
                      <div className="flex flex-row gap-2 justify-center">
                        <VideoPreview videoUrl={asset.url ?? ""} className="opacity-0">
                          <FiPlay />
                        </VideoPreview>
                        {
                          !listOnly && (
                            <p className="text-xs text-white truncate">{asset.name}</p>
                          )
                        }
                      </div>
                    </div>
                    <div
                      className={`absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-all ${_isSelected ? "scale-100" : "scale-0"
                        }`}
                    >
                      <MdDone />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex w-full items-center justify-center pt-4">
              {!publicAssetsSelected && hasNextPage && (
                <Button
                  size="sm"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? (
                    <CircularProgressIndicator />
                  ) : (
                    <IoReload />
                  )}
                  Load More
                </Button>
              )}
              {publicAssetsSelected && hasNextPublicPage && (
                <Button
                  size="sm"
                  disabled={isFetchingNextPublicPage}
                  onClick={() => fetchNextPublicPage()}
                >
                  {isFetchingNextPublicPage ? (
                    <CircularProgressIndicator />
                  ) : (
                    <IoReload />
                  )}
                  Load More
                </Button>
              )}
            </div>
          </div>
        </div>
      </QueryFetchStatePage>
    </QueryFetchStatePage>
  );

  function tab(title: string, selected: boolean, onClick: () => void) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`mr-2 flex cursor-pointer flex-row items-center gap-2 rounded-t-lg border-b-2 border-transparent p-2 transition-all ${selected
          ? "!border-accentColor text-accentColor dark:!border-darkAccentColor dark:text-darkAccentColor"
          : "hover:border-accentColor hover:border-opacity-50 hover:text-accentColor dark:hover:border-darkAccentColor dark:hover:border-opacity-50 dark:hover:text-darkAccentColor"
          }`}
      >
        {!listOnly ? title : undefined}
      </button>
    );
  }
}

export default SelectVideo;
